// src/components/PlannerProductExpanded.js
import React, { useState, useRef, useEffect } from "react"
import { IoClose, IoDuplicateSharp, IoScanOutline } from "react-icons/io5"
import placeholder from "../assets/placeholder.jpg"

// Helper function to detect if a hex color is light (needs dark border)
const isLightColor = (hexColor) => {
  if (!hexColor || !hexColor.startsWith("#")) return false
  const hex = hexColor.replace("#", "")
  const fullHex =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex
  const r = parseInt(fullHex.substring(0, 2), 16)
  const g = parseInt(fullHex.substring(2, 4), 16)
  const b = parseInt(fullHex.substring(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.85
}

const PlannerProductExpanded = ({
  pn,
  rotateProductNodeInstance,
  changeProductNodeInstanceHeight,
  applyMaterial,
  removeProductNodeInstance,
  duplicateProductNode,
  selectProductNodeInstance,
  onClose,
  language = "it",
}) => {
  const [expandedMaterial, setExpandedMaterial] = useState(null)
  const [materialContentHeight, setMaterialContentHeight] = useState(0)
  const materialContentRef = useRef(null)

  useEffect(() => {
    if (expandedMaterial && materialContentRef.current) {
      setMaterialContentHeight(materialContentRef.current.scrollHeight)
    } else {
      setMaterialContentHeight(0)
    }
  }, [expandedMaterial])

  const productName =
    pn.matchedLocalProduct?.name || pn.refData?.name || "Prodotto"
  const productPrice = pn.refData?.price || pn.matchedLocalProduct?.price || null

  const availableMaterialTypes =
    pn.configurableMaterials?.filter(
      (mat) => mat.materialVariations && mat.materialVariations.length > 1,
    ) || []

  const getCurrentVariation = (mat) => {
    const variationName = mat.appliedVariation || mat.activeVariation
    return mat.materialVariations?.find((v) => v.name === variationName)
  }

  const handleRotationChange = (value) => {
    rotateProductNodeInstance(pn)({
      target: { value },
      stopPropagation: () => {},
      preventDefault: () => {},
    })
  }

  const handleHeightDelta = (delta) => {
    const currentHeight = pn.height || 1
    const deltaAsHeight = delta / 100
    const newHeight =
      Math.round((currentHeight + deltaAsHeight) * 10000) / 10000
    changeProductNodeInstanceHeight(pn)({
      target: { value: newHeight * 100 },
      stopPropagation: () => {},
      preventDefault: () => {},
    })
  }

  const handleRotationDelta = (delta) => {
    const currentDeg = Math.round(((pn.rotationAngle || 0) * 180) / Math.PI)
    let newDeg = currentDeg + delta
    if (newDeg >= 360) newDeg -= 360
    if (newDeg < 0) newDeg += 360
    handleRotationChange(newDeg)
  }

  const currentRotation = Math.round(
    ((pn.rotationAngle || 0) * 180) / Math.PI,
  )
  const currentHeightPercent = Math.round(((pn.height || 1) - 1) * 100)

  const expandedMaterialData = expandedMaterial
    ? availableMaterialTypes.find((m) => m.name === expandedMaterial)
    : null

  return (
    <div className="ay-bg-gray-100 ay-rounded-2xl ay-overflow-hidden">
      {/* Header: name + price on top, actions row below */}
      <div className="ay-px-3 md:ay-px-4 ay-pt-3 md:ay-pt-4 ay-pb-2">
        <div className="ay-flex ay-items-start ay-justify-between ay-gap-2 md:ay-gap-3">
          <div className="ay-flex-1 ay-min-w-0">
            <h3 className="ay-text-sm md:ay-text-base ay-font-sans ay-font-semibold ay-text-gray-900 ay-leading-snug">
              {productName}
            </h3>
            {productPrice && (
              <span className="ay-text-sm md:ay-text-base ay-font-sans ay-font-bold ay-text-gray-900 ay-mt-0.5 md:ay-mt-1 ay-block">
                {productPrice.toLocaleString("it-IT", {
                  minimumFractionDigits: 2,
                })}{" "}
                EUR
              </span>
            )}
          </div>
          <div className="ay-flex ay-items-center ay-gap-0.5 md:ay-gap-1 ay-flex-shrink-0">
            {selectProductNodeInstance && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  selectProductNodeInstance(pn)()
                }}
                className="ay-w-7 ay-h-7 md:ay-w-8 md:ay-h-8 ay-flex ay-items-center ay-justify-center ay-text-gray-400 hover:ay-text-black ay-transition-colors ay-rounded-full hover:ay-bg-gray-200"
                title="Seleziona nel 3D"
              >
                <IoScanOutline className="ay-w-4 ay-h-4 md:ay-w-[17px] md:ay-h-[17px]" />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation()
                duplicateProductNode(pn)(e)
              }}
              className="ay-w-7 ay-h-7 md:ay-w-8 md:ay-h-8 ay-flex ay-items-center ay-justify-center ay-text-gray-400 hover:ay-text-blue-600 ay-transition-colors ay-rounded-full hover:ay-bg-gray-200"
              title="Duplica"
            >
              <IoDuplicateSharp className="ay-w-3.5 ay-h-3.5 md:ay-w-[15px] md:ay-h-[15px]" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                removeProductNodeInstance(pn)(e)
              }}
              className="ay-w-7 ay-h-7 md:ay-w-8 md:ay-h-8 ay-flex ay-items-center ay-justify-center ay-text-gray-400 hover:ay-text-red-600 ay-transition-colors ay-rounded-full hover:ay-bg-gray-200"
              title="Rimuovi"
            >
              <IoClose className="ay-w-4 ay-h-4 md:ay-w-[17px] md:ay-h-[17px]" />
            </button>
            <button
              onClick={onClose}
              className="ay-text-[10px] ay-font-serif ay-font-bold ay-text-gray-400 ay-uppercase ay-tracking-wider hover:ay-text-gray-700 ay-transition-colors ay-ml-1"
            >
              CHIUDI
            </button>
          </div>
        </div>
      </div>

      {/* Material types */}
      {availableMaterialTypes.length > 0 && (
        <div className="ay-border-t ay-border-gray-200">
          <div className="ay-flex ay-items-start ay-py-2 md:ay-py-3 ay-px-3 md:ay-px-4">
            <div className="ay-w-1/3 md:ay-w-1/5 ay-pt-1">
              <span className="ay-text-[12px] ay-font-serif ay-font-bold ay-text-gray-600 ay-uppercase ay-tracking-wider">
                FINITURE
              </span>
            </div>
            <div className="ay-w-2/3 md:ay-w-4/5">
              <div className="ay-flex ay-flex-wrap ay-gap-2">
                {availableMaterialTypes.map((mat) => {
                  const currentVar = getCurrentVariation(mat)
                  const isExpanded = expandedMaterial === mat.name
                  const needsDarkBorder =
                    currentVar?.thumbnail &&
                    isLightColor(currentVar.thumbnail)

                  return (
                    <button
                      key={mat.name}
                      onClick={() =>
                        setExpandedMaterial(isExpanded ? null : mat.name)
                      }
                      className="ay-group/mat ay-flex ay-flex-col ay-items-center"
                    >
                      <div className="ay-w-9 ay-h-9 ay-relative">
                        <div
                          className={`ay-absolute ay-overflow-hidden ay-transition-[inset] ay-duration-300 ay-ease-out ${
                            isExpanded
                              ? "ay-inset-[8%]"
                              : "ay-inset-[0%] group-hover/mat:ay-inset-[8%]"
                          }`}
                          style={{
                            border: needsDarkBorder
                              ? "1px solid #000"
                              : "none",
                          }}
                        >
                          {currentVar?.thumbnail?.startsWith("#") ? (
                            <div
                              className="ay-w-full ay-h-full"
                              style={{
                                backgroundColor: currentVar.thumbnail,
                              }}
                            />
                          ) : (
                            <img
                              src={currentVar?.thumbnail || placeholder}
                              alt={mat.name}
                              className="ay-w-full ay-h-full ay-object-cover ay-rotate-90"
                              onError={(e) => {
                                e.target.src = placeholder
                              }}
                            />
                          )}
                        </div>
                      </div>
                      <span
                        className={`ay-text-[8px] ay-font-serif ay-font-bold ay-uppercase ay-tracking-wider ay-mt-0.5 ay-transition-colors ay-duration-200 ${
                          isExpanded
                            ? "ay-text-black"
                            : "ay-text-gray-400 group-hover/mat:ay-text-gray-600"
                        }`}
                      >
                        {(mat.displayLabel || mat.name).substring(0, 10)}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Material variation grid (accordion) */}
      <div
        className="ay-overflow-hidden ay-transition-[max-height] ay-duration-300 ay-ease-in-out"
        style={{ maxHeight: `${materialContentHeight}px` }}
      >
        <div ref={materialContentRef} className="ay-px-3 md:ay-px-4 ay-pb-3">
          {expandedMaterialData && (
            <div className="ay-flex">
              <div className="ay-w-1/3 md:ay-w-1/5"></div>
              <div className="ay-w-2/3 md:ay-w-4/5">
                <div className="ay-border-t ay-border-gray-200 ay-pt-3 ay-mb-1"></div>
                <div className="ay-grid ay-grid-cols-4 ay-gap-1">
                  {expandedMaterialData.materialVariations.map((variation) => {
                    const isApplied =
                      expandedMaterialData.appliedVariation ===
                        variation.name ||
                      expandedMaterialData.activeVariation === variation.name
                    const thumbnail =
                      variation.thumbnail || variation.color || placeholder
                    const isColorSwatch =
                      thumbnail && thumbnail.startsWith("#")
                    const needsDarkBorder = isLightColor(thumbnail)

                    return (
                      <button
                        key={variation.name}
                        onClick={() =>
                          applyMaterial(
                            pn,
                            expandedMaterialData.name,
                            variation,
                          )
                        }
                        className="ay-group/var ay-cursor-pointer"
                        title={variation.displayName || variation.name}
                      >
                        <div className="ay-w-full ay-aspect-square ay-relative ay-flex ay-items-center ay-justify-center">
                          <div
                            className={`ay-absolute ay-overflow-hidden ay-transition-[inset] ay-duration-300 ay-ease-out ${
                              isApplied
                                ? "ay-inset-[10%]"
                                : "ay-inset-[0%] group-hover/var:ay-inset-[10%]"
                            }`}
                            style={{
                              border: needsDarkBorder
                                ? "1px solid #000"
                                : "none",
                            }}
                          >
                            {isColorSwatch ? (
                              <div
                                className="ay-w-full ay-h-full"
                                style={{ backgroundColor: thumbnail }}
                              />
                            ) : (
                              <img
                                src={thumbnail}
                                alt={variation.name}
                                className="ay-w-full ay-h-full ay-object-cover ay-rotate-90"
                                onError={(e) => {
                                  e.target.src = placeholder
                                }}
                              />
                            )}
                          </div>
                          <div
                            className={`ay-absolute ay-bottom-0 ay-left-0 ay-right-0 ay-text-center ay-transition-opacity ay-duration-200 ${
                              isApplied
                                ? "ay-opacity-100"
                                : "ay-opacity-0 group-hover/var:ay-opacity-100"
                            }`}
                          >
                            <span className="ay-text-black ay-font-serif ay-font-bold ay-text-[7px] ay-leading-none ay-truncate ay-block">
                              {(
                                variation.displayName || variation.name
                              ).substring(0, 12)}
                            </span>
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Rotation row */}
      <div className="ay-border-t ay-border-gray-200">
        <div className="ay-flex ay-items-center ay-py-2 md:ay-py-3 ay-px-3 md:ay-px-4">
          <div className="ay-w-1/3 md:ay-w-1/5">
            <span className="ay-text-[12px] ay-font-serif ay-font-bold ay-text-gray-600 ay-uppercase ay-tracking-wider">
              ROTAZIONE
            </span>
          </div>
          <div className="ay-w-2/3 md:ay-w-4/5 ay-flex ay-items-center ay-flex-wrap ay-gap-1.5">
            <button
              onClick={() => handleRotationDelta(-45)}
              className="ay-px-2 md:ay-px-3 ay-pt-2 ay-pb-1 md:ay-pt-2.5 md:ay-pb-1.5 ay-text-xs md:ay-text-sm ay-font-sans ay-font-medium ay-bg-gray-200 ay-rounded-md hover:ay-bg-gray-300 ay-transition-colors"
            >
              -45°
            </button>
            <button
              onClick={() => handleRotationDelta(-5)}
              className="ay-px-2 md:ay-px-3 ay-pt-2 ay-pb-1 md:ay-pt-2.5 md:ay-pb-1.5 ay-text-xs md:ay-text-sm ay-font-sans ay-font-medium ay-bg-gray-200 ay-rounded-md hover:ay-bg-gray-300 ay-transition-colors"
            >
              -5°
            </button>
            <span className="ay-px-2 md:ay-px-3 ay-pt-2 ay-pb-1 md:ay-pt-2.5 md:ay-pb-1.5 ay-text-xs md:ay-text-sm ay-font-sans ay-font-medium ay-bg-white ay-border ay-border-gray-300 ay-rounded-md ay-min-w-[44px] md:ay-min-w-[52px] ay-text-center">
              {currentRotation}°
            </span>
            <button
              onClick={() => handleRotationDelta(5)}
              className="ay-px-2 md:ay-px-3 ay-pt-2 ay-pb-1 md:ay-pt-2.5 md:ay-pb-1.5 ay-text-xs md:ay-text-sm ay-font-sans ay-font-medium ay-bg-gray-200 ay-rounded-md hover:ay-bg-gray-300 ay-transition-colors"
            >
              +5°
            </button>
            <button
              onClick={() => handleRotationDelta(45)}
              className="ay-px-2 md:ay-px-3 ay-pt-2 ay-pb-1 md:ay-pt-2.5 md:ay-pb-1.5 ay-text-xs md:ay-text-sm ay-font-sans ay-font-medium ay-bg-gray-200 ay-rounded-md hover:ay-bg-gray-300 ay-transition-colors"
            >
              +45°
            </button>
          </div>
        </div>
      </div>

      {/* Height row */}
      <div className="ay-border-t ay-border-gray-200">
        <div className="ay-flex ay-items-center ay-py-2 md:ay-py-3 ay-px-3 md:ay-px-4">
          <div className="ay-w-1/3 md:ay-w-1/5">
            <span className="ay-text-[12px] ay-font-serif ay-font-bold ay-text-gray-600 ay-uppercase ay-tracking-wider">
              ALTEZZA
            </span>
          </div>
          <div className="ay-w-2/3 md:ay-w-4/5 ay-flex ay-items-center ay-flex-wrap ay-gap-1.5">
            <button
              onClick={() => handleHeightDelta(-10)}
              className="ay-px-2 md:ay-px-3 ay-pt-2 ay-pb-1 md:ay-pt-2.5 md:ay-pb-1.5 ay-text-xs md:ay-text-sm ay-font-sans ay-font-medium ay-bg-gray-200 ay-rounded-md hover:ay-bg-gray-300 ay-transition-colors"
            >
              -10
            </button>
            <button
              onClick={() => handleHeightDelta(-1)}
              className="ay-px-2 md:ay-px-3 ay-pt-2 ay-pb-1 md:ay-pt-2.5 md:ay-pb-1.5 ay-text-xs md:ay-text-sm ay-font-sans ay-font-medium ay-bg-gray-200 ay-rounded-md hover:ay-bg-gray-300 ay-transition-colors"
            >
              -1
            </button>
            <span className="ay-px-2 md:ay-px-3 ay-pt-2 ay-pb-1 md:ay-pt-2.5 md:ay-pb-1.5 ay-text-xs md:ay-text-sm ay-font-sans ay-font-medium ay-bg-white ay-border ay-border-gray-300 ay-rounded-md ay-min-w-[44px] md:ay-min-w-[52px] ay-text-center">
              {currentHeightPercent}%
            </span>
            <button
              onClick={() => handleHeightDelta(1)}
              className="ay-px-2 md:ay-px-3 ay-pt-2 ay-pb-1 md:ay-pt-2.5 md:ay-pb-1.5 ay-text-xs md:ay-text-sm ay-font-sans ay-font-medium ay-bg-gray-200 ay-rounded-md hover:ay-bg-gray-300 ay-transition-colors"
            >
              +1
            </button>
            <button
              onClick={() => handleHeightDelta(10)}
              className="ay-px-2 md:ay-px-3 ay-pt-2 ay-pb-1 md:ay-pt-2.5 md:ay-pb-1.5 ay-text-xs md:ay-text-sm ay-font-sans ay-font-medium ay-bg-gray-200 ay-rounded-md hover:ay-bg-gray-300 ay-transition-colors"
            >
              +10
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PlannerProductExpanded
